<template>
  <div v-if="isUsable">
    <page-separator title="Page Layout" />

    <b-form-group label-class="form-label">
      <div class="row">
        <div class="col-md-3 col-6" v-for="layout in pageLayoutOptions" :key="`${pageKey}-${layout}`">
          <div class="layout-thumb text-center clickable-item mb-3" @click.prevent="selected = layout">
            <figure class="mb-1">
              <!-- 'Default' is the page's existing markup, not a gallery layout, so it
                   has no screenshot — show a tile rather than a misleading mock-up. -->
              <div
                v-if="layout === DEFAULT_PAGE_LAYOUT"
                class="layout-thumb__current d-flex flex-column align-items-center justify-content-center"
                :class="selected === layout ? 'border-outline-primary' : 'layout-thumb__current--idle'"
              >
                <i class="material-icons text-muted">check_circle_outline</i>
                <small class="text-muted mt-1">Keep as-is</small>
              </div>
              <img
                v-else
                width="100%"
                :src="getPageLayoutImg(pageKey, layout)"
                :alt="`${pageLayoutLabels[layout] || layout} preview`"
                :class="selected === layout ? 'border-outline-primary' : ''"
                :style="
                  selected === layout
                    ? 'object-fit: cover; object-position: top;'
                    : 'border: #f2f2f2 2px solid; border-radius: 8px; object-fit: cover; object-position: top;'
                "
              />
            </figure>
            <small :class="selected === layout ? 'text-primary font-weight-bold' : 'text-muted'">
              {{ pageLayoutLabels[layout] || layout }}
            </small>
          </div>
        </div>
      </div>
      <small class="text-muted d-block">
        Choose how this page is arranged on your website. &quot;Current design&quot; leaves it exactly as it looks
        today &mdash; your content, photos, and text are the same either way, only the arrangement changes.
      </small>
    </b-form-group>

    <div class="d-flex justify-content-end mt-3">
      <b-btn variant="primary" type="button" class="btn-normal" :disabled="isSaving" @click="savePageLayout">
        <b-spinner small v-if="isSaving" class="mr-2" />
        <span>{{ isSaving ? 'Saving' : 'Save Page Layout' }}</span>
      </b-btn>
    </div>
  </div>
</template>

<script>
import { get } from 'lodash';
import { mapActions, mapGetters } from 'vuex';

import PageSeparator from '@/components/Ui/PageSeparator.vue';
import { USER_ROLES } from '@/common/constants';
import { PAGE_LAYOUT_OPTIONS, PAGE_LAYOUT_LABELS, DEFAULT_PAGE_LAYOUT, pageLayoutImage } from '@/common/pageLayouts';

/**
 * Layout picker for a single inner page (About, Tuition, Gallery, ...).
 *
 * The choice lives on the school's LinkedWebsite (`page_layouts`), so this reads
 * through getBasicConfig and writes the one page it owns via update-page-layout,
 * mirroring how the Section Style panels persist `*_page_style`.
 */
export default {
  name: 'PageLayoutPicker',

  components: { PageSeparator },

  props: {
    // Page key on LinkedWebsite.page_layouts — 'about', 'tuition', 'locations', ...
    pageKey: { type: String, required: true },
    // Super-admin's selected school. School users leave this null.
    schoolId: { type: Number, default: null },
  },

  data() {
    return {
      selected: DEFAULT_PAGE_LAYOUT,
      DEFAULT_PAGE_LAYOUT,
      isSaving: false,
      pageLayoutOptions: PAGE_LAYOUT_OPTIONS,
      pageLayoutLabels: PAGE_LAYOUT_LABELS,
    };
  },

  computed: {
    ...mapGetters('auth', ['getLoggedInUser']),

    isSuperAdmin() {
      return get(this.getLoggedInUser, 'role') === USER_ROLES.SUPER_ADMIN;
    },

    /** Super admins must pick a school before there's a website to configure. */
    isUsable() {
      return !this.isSuperAdmin || !!this.schoolId;
    },
  },

  watch: {
    schoolId: {
      immediate: true,
      handler() {
        this.fetchPageLayout();
      },
    },
  },

  methods: {
    ...mapActions('webConfig', ['getBasicConfig', 'updatePageLayout']),

    getPageLayoutImg: pageLayoutImage,

    async fetchPageLayout() {
      if (!this.isUsable) return;
      try {
        const config = await this.getBasicConfig(this.isSuperAdmin ? { school_id: this.schoolId } : {});
        this.selected = get(config, `page_layouts.${this.pageKey}`) || DEFAULT_PAGE_LAYOUT;
      } catch (e) {
        // Silent fail so a layout lookup never blocks the page editor from loading.
      }
    },

    async savePageLayout() {
      this.isSaving = true;
      try {
        await this.updatePageLayout({
          school_id: this.isSuperAdmin ? this.schoolId : null,
          page: this.pageKey,
          layout: this.selected,
        });
        this.makeToast({ variant: 'success', msg: 'Page layout updated!' });
      } catch (e) {
        this.makeToast({ variant: 'danger', msg: this.$t('generalMsgs.genErrorMsg') });
      } finally {
        this.isSaving = false;
      }
    },
  },
};
</script>

<style lang="css" scoped>
.layout-thumb__current {
  aspect-ratio: 480 / 258;
  border-radius: 8px;
  background: #fafbfc;
  transition: transform 0.2s ease-in-out;
}

.layout-thumb__current--idle {
  border: #f2f2f2 2px solid;
}

.layout-thumb:hover .layout-thumb__current {
  transform: scale(1.08);
}

.layout-thumb figure img {
  transition: transform 0.2s ease-in-out;
  border-radius: 8px;
}

.layout-thumb:hover figure img {
  transform: scale(1.08);
}
</style>
